import Card from '../../components/card/Card';
import { balanceInfo, clientData, indicator, riskData } from '../../types/interfaces';
import { useClientTheme } from '../../context/ThemeContext';
import {
  RISK_CODES,
  APPLICATION_STATUS,
  initialClientData,
} from '../../utils/Constants';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';
import CheckLightResume from './CheckLight/ChecklightResume';
import LidecoSemaforo from './CheckLight/LidecoSemaforo';
import { useRef, useState, useEffect, useMemo } from 'react';
import Modal from '../../components/modal/modal';
import { getProductLines } from '../../services/ClientService';
import { parseSelectableProductLines, type SelectableProductLine } from '../../utils/productLines';
import { resolveSolicitudProducts } from '../../utils/solicitudProducts';
import TextArea from '../../components/textarea/TextArea';
import { updateApplication } from '../../services/ApplicationService';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../../components/toast/toast';
import Spinner from '../../components/Spinner/spinner';
import Button from '../../components/button/Button';
import P from '../../components/texts/p/P';
import {
  navigateAfterFlowAction,
  type DetailLocationState,
} from '../../utils/listNavigationState';
import useStatus from '../../hooks/useStatus';
import { hasError, isSuccessfulResponse } from '../../utils/Utils';
import H from '../../components/texts/h/H';
import SummaryAmounts from '../common/SummaryAmounts';
import CreditoMaximoSection, { CreditoMaximoSectionHandle } from './CreditoMaximo/CreditoMaximoSection';
import { useRiskPdf } from '../../hooks/useRiskPdf';
import RiskAnalysisPdf from './Pdf/RiskAnalysisPdf';
import { useReferrerNames } from '../../hooks/useReferrerNames';
import { useOfficers } from '../../hooks/useOfficers';
import { useConfig } from '../../context/ConfigContext';
import { useManagements } from '../../hooks/useManagements';
import { useOperationTypes } from '../../hooks/useOperationTypes';
import useRole from '../../hooks/useRole';
import { resolveManagementCode } from '../../utils/managementAssetRules';
import { useTenantUiSettings } from '../../hooks/useTenantUiSettings';
import AtlanticChecklightLayout from './CheckLight/AtlanticChecklightLayout';
import AtlanticCompanyHeader from './CheckLight/AtlanticCompanyHeader';
import AtlanticMotivoField from './CheckLight/AtlanticMotivoField';
import { resolveAtlanticReferrerDisplayName } from './CheckLight/resolveAtlanticReferrerDisplayName';
import { useCommitteeCreditoMaximo } from '../../hooks/useCommitteeCreditoMaximo';

interface CheckLightProps {
  riskData: riskData | undefined;
  isLoading: boolean;
  handleRiskData: Function;
  handleRiskDataAndDebts?: Function;
  cuit: string | undefined;
  applicationId: string | undefined;
  files: any;
  legalEntity: any;
  applicationStatus: number;
  debts: any;
  declineOrChangeBtn?: boolean;
  hideInfo?: boolean;
  balanceData?: balanceInfo;
  postBalanceData?: any;
  clientData?: clientData;
  showAmounts?: boolean;
  /** true: calculador habilitado y Solicitud se muestra después de la info financiera */
  isMaxCreditCalculatorEnabled?: boolean;
  /** Controla visibilidad de columnas Activo/Cantidad (cotizaciones) */
  isQuotationsEnabled?: boolean;
  // Dual balance props (Cementos feature)
  showDualBalance?: boolean;
  previousBalanceData?: balanceInfo;
  /** Indicadores BPB del período anterior; se alimenta cuando el backend exponga el endpoint. */
  previousBpbIndicators?: indicator[];
  /** Cementos Uruguay: Central LIDECO + moneda UYU en Solicitud. */
  isUruguayEnabled?: boolean;
}

const RiskResume: React.FC<CheckLightProps> = ({
  riskData,
  isLoading,
  handleRiskData,
  handleRiskDataAndDebts,
  cuit,
  applicationId,
  files,
  applicationStatus,
  debts,
  declineOrChangeBtn = true,
  hideInfo = true,
  balanceData,
  postBalanceData,
  clientData,
  showAmounts = true,
  isMaxCreditCalculatorEnabled = false,
  isQuotationsEnabled = false,
  // Dual balance props (Cementos feature)
  showDualBalance = false,
  previousBalanceData,
  previousBpbIndicators,
  isUruguayEnabled = false,
}) => {
  const { clientTheme } = useClientTheme();
  const { referrerNames } = useReferrerNames();
  const { officers } = useOfficers();
  const appConfig = useConfig();
  const { isAtlanticLayout, summary: summaryUiSettings } = useTenantUiSettings(true);
  const isManagementSelectionEnabled = appConfig.isManagementSelectionEnabled ?? false;
  // Currency SoT is login config — not isUruguayEnabled (layout-only).
  const localCurrencyCode = resolveLocalCurrencyCode(appConfig);
  const { managements } = useManagements(isManagementSelectionEnabled);
  const { operationTypes } = useOperationTypes();
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [isSendingApplication, setIsSendingApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state ?? {}) as DetailLocationState;
  const [isSendingToCommittee, setIsSendingToCommittee] = useState(false);
  const [openCommitteeModal, setOpenCommitteeModal] = useState(false);
  const { isInCommittee } = useStatus({ applicationStatus });
  const { canTransitionRisk } = useRole();
  const [clientInfo, setClientInfo] = useState<clientData>(
    clientData || initialClientData
  );
  const pdfRef = useRef<HTMLDivElement>(null);
  const creditoMaximoRef = useRef<CreditoMaximoSectionHandle>(null);
  const [isCreditoSaving, setIsCreditoSaving] = useState(false);
  const { isGenerating, generatePdfFromNode } = useRiskPdf({
    fileName: `analisis_riesgo_${cuit || 'cliente'}.pdf`,
    useImprovedPdfExport: showDualBalance,
  });
  const operationTypeLabel = useMemo(() => {
    if (clientInfo.operation_type_label) return clientInfo.operation_type_label;
    if (!clientInfo.operation_type_id) return null;
    return operationTypes.find(ot => ot.id === clientInfo.operation_type_id)?.label || null;
  }, [clientInfo.operation_type_id, clientInfo.operation_type_label, operationTypes]);

  const [productLines, setProductLines] = useState<SelectableProductLine[]>([]);
  const [analystNotes, setAnalystNotes] = useState<string>(clientData?.analyst_notes || '');

  useEffect(() => {
    if (clientData) {
      setClientInfo(clientData);
    }
  }, [clientData]);

  /** Solicitud vigente (sin líneas ORIGINATION deshabilitadas): ver `resolveSolicitudProducts`. */
  const clientDataForSolicitud = useMemo(
    () => ({
      ...clientInfo,
      products: resolveSolicitudProducts(clientInfo),
    }),
    [clientInfo]
  );

  const managementCode = resolveManagementCode(
    clientDataForSolicitud.management_id,
    managements
  );

  const resumeApplicationReady = Boolean(applicationId && clientInfo.products?.length);

  const {
    state: resumeCreditoState,
    productLines: resumeCreditoProductLines,
    availableAssets: resumeCreditoAvailableAssets,
    superaMaximo: resumeCreditoSuperaMaximo,
  } = useCommitteeCreditoMaximo(
    cuit,
    applicationId,
    isMaxCreditCalculatorEnabled,
    clientInfo.products ?? [],
    isQuotationsEnabled,
    0,
    resumeApplicationReady
  );

  // Fetch product lines for the PDF
  useEffect(() => {
    getProductLines()
      .then((response) => {
        if (response && 'data' in response) {
          setProductLines(parseSelectableProductLines(response.data));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch product lines:', error);
      });
  }, []);

  // Load analyst notes from clientData
  useEffect(() => {
    if (clientData?.analyst_notes !== undefined) {
      setAnalystNotes(clientData.analyst_notes || '');
    }
  }, [clientData?.analyst_notes]);

  const handleToSendToCommittee = async (status: string) => {
    setIsSendingToCommittee(true);

    if (isMaxCreditCalculatorEnabled && creditoMaximoRef.current) {
      const saved = await creditoMaximoRef.current.save();
      if (!saved) {
        setIsSendingToCommittee(false);
        return;
      }
    }

    await updateApplication(
      cuit || '',
      { status_code: status, comment: modalText, analyst_notes: analystNotes },
      applicationId || ''
    ).then((response) => {
      if (isSuccessfulResponse(response)) {
        useToast.success({
          title: 'Enviaste la solicitud. El comité será notificado.',
          id: 'risk-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, locationState);
        }, 1000);
      } else if (hasError(response).isAnyError) {
        useToast.error({
          title: 'Ocurrió un error inesperado. Intentá nuevamente.',
          id: 'risk-toast',
          description: hasError(response).isClientError
            ? 'Error de cliente'
            : 'Error técnico',
          closeButton: false,
          autoClose: 5000,
        });
      }
      setIsSendingToCommittee(false);
      setOpenCommitteeModal(false);
    });
  };

  const handleDownloadPdf = async () => {
    await generatePdfFromNode(pdfRef.current);
  };

  const handleNotesChange = (value: string) => {
    setAnalystNotes(value);
  };

  const sendToCommitteeModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Al enviar la solicitud, esta pasará al comité que tomará la decisión
          de aprobarla o no.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí un comentario sobre la solicitud o dejalo en blanco.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const renderProductsAndRequestAmount = (clientData: clientData) => {
    const motivoEditable =
      isAtlanticLayout && summaryUiSettings?.motivo?.editable === true;

    return (
      <Card margin={{ bottom: '5' }}>
        <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
          <H
            type='4'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            Solicitud
          </H>
        </Card>
        {motivoEditable && cuit && applicationId ? (
          <AtlanticMotivoField
            cuit={cuit}
            applicationId={applicationId}
            clientData={clientData}
            initialReason={clientData.reason}
            editable
            onSaved={(reason) => {
              setClientInfo((prev) => ({ ...prev, reason }));
            }}
          />
        ) : clientData.reason?.trim() ? (
          <Card display='flex' gap='4px'>
            <P type='2' variant='neutral' weight='bold'>
              Motivo:
            </P>
            <P type='2' variant='neutral'>
              {clientData.reason}
            </P>
          </Card>
        ) : null}
        {operationTypeLabel && (
          <Card display='flex' gap='4px'>
            <P type='2' variant='neutral' weight='bold'>
              Tipo de operación:
            </P>
            <P type='2' variant='neutral'>
              {operationTypeLabel}
            </P>
          </Card>
        )}
        <Card margin={{ top: '4' }} width='100%' maxWidth='100%'>
          <div
            style={{
              padding: '0 12px',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <SummaryAmounts
              clientData={clientData}
              isManualAmount={clientData.manual_total_amount}
              products={clientData.products}
              amounts={clientData.requested_amount}
              hideFianzaPagare={hideInfo}
              localCurrencyCode={localCurrencyCode}
            />
          </div>
        </Card>
      </Card>
    );
  };

  const handleApplication = (status: string) => {
    setIsSendingApplication(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.approved.label,
        'Aceptaste la documentación. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.pending.label,
        'Solicitaste cambios en la solicitud. El productor será notificado.'
      );

      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'risk-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, locationState);
        }, 1000);
      }
      setIsSendingApplication(false);
    });
  };

  return (
    <Card spacing={{ left: isUruguayEnabled ? '0' : '6', top: '4' }} style={isUruguayEnabled ? { maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' } : undefined}>
      <RiskAnalysisPdf
        ref={pdfRef}
        clientData={clientDataForSolicitud}
        riskData={riskData}
        balanceData={balanceData}
        postBalanceData={postBalanceData}
        debts={debts}
        documents={files}
        cuit={cuit}
        referrerNames={referrerNames}
        officers={officers}
        productLines={productLines}
        managements={managements}
        isManagementSelectionEnabled={isManagementSelectionEnabled}
        isOriginationDataModalEnabled={appConfig.isOriginationDataModalEnabled ?? false}
        sgrDistributions={clientInfo?.sgr_distributions}
        showDualBalance={showDualBalance}
        previousBalanceData={previousBalanceData}
        previousBpbIndicators={previousBpbIndicators}
        isUruguayEnabled={isUruguayEnabled}
        localCurrencyCode={localCurrencyCode}
        creditoMaximoState={resumeCreditoState}
        creditoProductLines={resumeCreditoProductLines}
        creditoSuperaMaximo={resumeCreditoSuperaMaximo}
        isQuotationsEnabled={isQuotationsEnabled}
        creditoAvailableAssets={resumeCreditoAvailableAssets}
      />
      <Modal
        isOpen={openCommitteeModal}
        onClose={() => setOpenCommitteeModal(false)}
        title={'Enviar solicitud a comité'}
        primaryText='Enviar a comité'
        primaryVariant='primary'
        secondaryText='Cancelar'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setOpenCommitteeModal(false);
        }}
        onPrimaryAction={() => {
          handleToSendToCommittee(APPLICATION_STATUS.committee.label);
        }}
        loadingPrimaryBtn={isSendingToCommittee}
        children={sendToCommitteeModal()}
      />
      <Modal
        isOpen={isRejectApplication}
        onClose={() => setIsRejectApplication(false)}
        title={'Declinar o solicitar cambios'}
        primaryText='Solicitar cambios'
        primaryVariant='primary'
        secondaryText='Declinar solicitud'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.rejected.label);
        }}
        onPrimaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.pending.label);
        }}
        children={renderRejectModal()}
      />

      {(() => {
        const referrerLabel = resolveAtlanticReferrerDisplayName(
          clientInfo.referrer_name,
          referrerNames,
        );
        const companyHeaderNode = isAtlanticLayout ? (
          <AtlanticCompanyHeader
            companyName={clientInfo.basic_data?.company_name}
            activity={clientInfo.basic_data?.activity}
            referrer={referrerLabel}
            showCompanyName={summaryUiSettings?.companyHeader?.showCompanyName !== false}
            showActivity={summaryUiSettings?.companyHeader?.showActivity !== false}
            showReferrer={summaryUiSettings?.companyHeader?.showReferrer !== false}
          />
        ) : null;

const solicitudNode =
          showAmounts && !hideInfo
            ? renderProductsAndRequestAmount(clientDataForSolicitud)
            : null;

        const checkLightNode = (
          <>
            <CheckLightResume
              data={riskData}
              showBalanceInfo={true}
              isLoading={isLoading}
              handleRiskData={handleRiskData}
              handleRiskDataAndDebts={isUruguayEnabled ? undefined : handleRiskDataAndDebts}
              riskCodes={
                isUruguayEnabled
                  ? [RISK_CODES.bpb]
                  : [RISK_CODES.bpb, RISK_CODES.casfog, RISK_CODES.bcra]
              }
              debts={debts}
              documents={files}
              cuit={cuit}
              applicationId={applicationId}
              hideInfo={hideInfo}
              hideArBureauSections={isUruguayEnabled}
              showFinancialIndicators={true}
              balanceData={balanceData}
              postBalanceData={postBalanceData}
              onDownloadPdf={handleDownloadPdf}
              isPdfLoading={isGenerating}
              showDualBalance={showDualBalance}
              previousBalanceData={previousBalanceData}
              previousBpbIndicators={previousBpbIndicators}
              analystNotes={analystNotes}
              hideNotes={hideInfo}
            />
            {isUruguayEnabled && (
              <LidecoSemaforo
                riskData={Array.isArray(riskData) ? riskData : undefined}
                isLoading={isLoading}
                onRefresh={() => handleRiskData([RISK_CODES.lideco], true)}
                hideRefresh={hideInfo}
              />
            )}
          </>
        );

        if (isAtlanticLayout) {
          return (
            <AtlanticChecklightLayout
              sectionOrder={summaryUiSettings?.sectionOrder}
              sections={{
                companyHeader: companyHeaderNode,
                solicitud: solicitudNode,
                // Remaining PRD sections live inside CheckLightResume (nosis/balance/debts).
                nosisScores: checkLightNode,
              }}
            />
          );
        }

        return (
          <>
            {!isMaxCreditCalculatorEnabled && solicitudNode}
            {checkLightNode}
            {isMaxCreditCalculatorEnabled && solicitudNode}
          </>
        );
      })()}

      {isMaxCreditCalculatorEnabled && cuit && applicationId && (
        <CreditoMaximoSection
          ref={creditoMaximoRef}
          cuit={cuit}
          applicationId={applicationId}
          products={clientDataForSolicitud.products ?? []}
          isQuotationsEnabled={isQuotationsEnabled}
          hideSaveButton
          onSavingChange={setIsCreditoSaving}
          managementCode={managementCode}
          localCurrencyCode={localCurrencyCode}
        />
      )}

      <Card margin={{ top: '5' }}>
        <Card
          variant={clientTheme ? 'primary' : 'success'}
          modifier='weak'
          spacing={'2'}
        >
          <H
            type='3'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            Notas
          </H>
        </Card>
        <Card margin={{ top: '3' }}>
          {hideInfo ? (
            <P type='2' variant='neutral' withMargin={false}>
              {analystNotes && analystNotes.trim() !== ''
                ? analystNotes
                : 'sin comentarios'}
            </P>
          ) : (
            <TextArea
              value={analystNotes}
              placeHolder='Escribí tus notas aquí...'
              maxLengthHelpText={false}
              onChange={(e) => handleNotesChange(e.target.value)}
            />
          )}
        </Card>
      </Card>
      <Card
        display='flex'
        justifyContent='space-between'
        margin={{ top: '5' }}
        gap='16px'
      >
        {declineOrChangeBtn && canTransitionRisk && !isInCommittee && !hideInfo && (
          <Card width='280px'>
            <Button
              id='checkRisk-button-reject'
              size='lg'
              variant='secondary'
              onClick={() => setIsRejectApplication(true)}
              fullWidth
              disabled={isSendingApplication}
            >
              {isSendingApplication ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                'Declinar o solicitar cambios'
              )}
            </Button>
          </Card>
        )}
        <Card display='flex' gap='16px'>
          {isMaxCreditCalculatorEnabled && cuit && applicationId && (
            <Card width='160px'>
              <Button
                id='credito-maximo-guardar'
                size='lg'
                variant='primary'
                fullWidth
                onClick={() => creditoMaximoRef.current?.save()}
                disabled={isCreditoSaving}
              >
                {isCreditoSaving ? <Spinner size='sm' variant='primary' /> : 'Guardar'}
              </Button>
            </Card>
          )}
          {canTransitionRisk && !isInCommittee && !hideInfo && (
            <Card width='200px'>
              <Button
                id='checkRisk-button-send-to-committee'
                size='lg'
                variant='primary'
                fullWidth
                onClick={() => setOpenCommitteeModal(true)}
              >
                Enviar a comité
              </Button>
            </Card>
          )}
        </Card>
      </Card>
    </Card>
  );
};

export default RiskResume;
